import React, { useEffect, useState } from 'react';
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import { formatTimeUntil, getScheduledRoom } from '../../services/roomService';
import './RoomNotAvailableModal.css';

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatRange(start, end) {
  const a = formatWhen(start);
  const b = formatWhen(end);
  if (a && b) return `${a} – ${b}`;
  return a || b || '—';
}

export default function RoomNotAvailableModal({
  isOpen,
  onClose,
  room,
  onBackToHome,
  onJoinRoom,
  participantName,
}) {
  // Full-page iframe state — do not dim GetGeek header/sidebar via meetgeek-dialog.
  const [now, setNow] = useState(() => new Date());
  const [timeUntilStart, setTimeUntilStart] = useState(null);

  useEffect(() => {
    if (!isOpen || !room) return undefined;

    const tick = () => {
      const current = new Date();
      setNow(current);
      const startTime = new Date(room.scheduledStartTime);
      const endTime = new Date(room.scheduledEndTime);
      if (current < startTime) setTimeUntilStart(startTime - current);
      else if (current > endTime) setTimeUntilStart(null);
      else setTimeUntilStart(0);
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [isOpen, room]);

  if (!isOpen || !room) return null;

  const startTime = new Date(room.scheduledStartTime);
  const endTime = new Date(room.scheduledEndTime);
  const isEnded = now > endTime;
  const isUpcoming = now < startTime;
  const isAvailable = !isEnded && !isUpcoming;
  const title = room.title && room.title !== 'N/A' ? room.title : 'Meeting';
  const stateClass = isEnded ? 'is-ended' : isAvailable ? 'is-live' : '';

  const handleBack = () => {
    (onBackToHome || onClose)?.();
  };

  return (
    <div className="mg-rna-page" role="dialog" aria-modal="true">
      <div className={`mg-rna-card ${stateClass}`}>
        <div className="mg-rna-icon" aria-hidden>
          {isEnded ? (
            <ErrorOutlineOutlined sx={{ fontSize: 30 }} />
          ) : isAvailable ? (
            <CheckCircleOutlineOutlined sx={{ fontSize: 30 }} />
          ) : (
            <ScheduleOutlined sx={{ fontSize: 30 }} />
          )}
        </div>

        <h2>
          {isEnded
            ? 'This meeting has ended'
            : isAvailable
              ? 'Room is now available'
              : "This meeting hasn't started"}
        </h2>

        <p>
          {isEnded
            ? 'The scheduled time for this meeting is over.'
            : isAvailable
              ? 'The meeting window is open — you can join now.'
              : `You can join once the meeting opens at ${formatWhen(startTime)}.`}
        </p>

        {isUpcoming && timeUntilStart != null && timeUntilStart > 0 && (
          <div className="mg-rna-countdown">
            <div className="mg-rna-countdown-label">Opens in</div>
            <div className="mg-rna-countdown-value">
              {formatTimeUntil(timeUntilStart)}
            </div>
          </div>
        )}

        {isAvailable && <div className="mg-rna-live">Meeting is live</div>}

        <dl className="mg-rna-details">
          <div>
            <dt>Meeting</dt>
            <dd>{title}</dd>
          </div>
          {room.hostName ? (
            <div>
              <dt>Host</dt>
              <dd>{room.hostName}</dd>
            </div>
          ) : null}
          <div>
            <dt>{isEnded ? 'Was scheduled' : 'Scheduled'}</dt>
            <dd>{formatRange(startTime, endTime)}</dd>
          </div>
        </dl>

        <div className="mg-rna-actions">
          {isAvailable && onJoinRoom ? (
            <button
              type="button"
              className="mg-rna-primary"
              onClick={() => {
                const name =
                  (participantName || '').trim() || room.hostName || '';
                onJoinRoom(room.roomName, name, true);
              }}
            >
              Join now
            </button>
          ) : null}
          <button
            type="button"
            className={isAvailable ? 'mg-rna-secondary' : 'mg-rna-primary'}
            onClick={handleBack}
          >
            Back to home
          </button>
        </div>
      </div>
    </div>
  );
}

export function LinkBootstrapScreen() {
  return (
    <div className="mg-rna-boot" aria-busy="true" aria-live="polite">
      <div className="mg-rna-boot-inner">
        <div className="mg-rna-spinner" />
        <span>Opening meeting…</span>
      </div>
    </div>
  );
}

/**
 * After Leave on an invite link (no GetGeek home context).
 * Rejoin while the meeting window is open; otherwise show ended.
 */
export function PostLeaveMeetingScreen({ session, onRejoin, rejoining = false }) {
  const [now, setNow] = useState(() => new Date());
  const [endTime, setEndTime] = useState(() =>
    session?.scheduledEndTime ? new Date(session.scheduledEndTime) : null
  );
  const [status, setStatus] = useState('');
  const [title, setTitle] = useState(session?.title || 'Meeting');

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!session?.roomName) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await getScheduledRoom(session.roomName);
        const room = data?.room;
        if (cancelled || !room) return;
        if (room.title) setTitle(room.title);
        if (room.scheduledEndTime) setEndTime(new Date(room.scheduledEndTime));
        if (room.status) setStatus(room.status);
      } catch {
        /* keep snapshot from leave */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.roomName]);

  if (!session?.roomName) return null;

  const endedByStatus = status === 'ended';
  const endedByTime =
    endTime instanceof Date && !Number.isNaN(endTime.getTime()) && now > endTime;
  const isEnded = endedByStatus || endedByTime;

  return (
    <div className="mg-rna-page" role="status">
      <div className={`mg-rna-card ${isEnded ? 'is-ended' : 'is-live'}`}>
        <div className="mg-rna-icon" aria-hidden>
          {isEnded ? (
            <ErrorOutlineOutlined sx={{ fontSize: 30 }} />
          ) : (
            <CheckCircleOutlineOutlined sx={{ fontSize: 30 }} />
          )}
        </div>

        <h2>{isEnded ? 'This meeting has ended' : 'You left the meeting'}</h2>
        <p>
          {isEnded
            ? 'The meeting is no longer available to join.'
            : 'The meeting is still in progress. You can rejoin anytime.'}
        </p>

        {!isEnded && title ? (
          <dl className="mg-rna-details">
            <div>
              <dt>Meeting</dt>
              <dd>{title}</dd>
            </div>
          </dl>
        ) : null}

        {!isEnded && typeof onRejoin === 'function' ? (
          <div className="mg-rna-actions">
            <button
              type="button"
              className="mg-rna-primary"
              disabled={rejoining}
              onClick={() => onRejoin(session)}
            >
              {rejoining ? 'Rejoining…' : 'Rejoin'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
